import { Box, Typography } from "@mui/material";
import { Attachments, TaxonomyUploadZone } from "./Attachments";
import { CollapsibleSectionCard } from "./CollapsibleSectionCard";
import { RequestFormContext } from "../RequestFormContext";
import { useTheme } from "@mui/material/styles";
import { CardDocumentIcon, getYCenterStyles } from "vig-ui-library";
import { shouldHideCategory } from "../Utilities/mappers";
import { areAttachmentUploadDropzonesGloballyVisible } from "../Utilities/attachmentUploadVisibility";
import { FileTile } from "./FileTile";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { TaxonomyDto } from "../Schemas/TaxonomyDto";
import { uploadLogic } from "../Utilities/UploadHelpers";
import { createRequestFormFileUploader } from "../Utilities/FileUploaderFactory";
import { getAttachmentFileTaxonomyId } from "../Utilities/fileTaxonomy";

type AttachmentsSectionProps = {
    attachmentUploadMessageVisible: boolean;
    requestFormContext: Omit<RequestFormContext, "pathToProp">;
};

function fileBelongsToVisibleAttachmentCategories(file: RequestFileDto, visibleCategoryIds: Set<number>): boolean {
    const taxonomyId = getAttachmentFileTaxonomyId(file);
    if (taxonomyId == null) return true;
    return visibleCategoryIds.has(taxonomyId);
}

function getTaxonomyMaxFilesLimit(taxonomy: TaxonomyDto): number | null {
    const maxNumberOfFiles = taxonomy.maxNumberOfFiles;
    if (maxNumberOfFiles == null || !Number.isFinite(maxNumberOfFiles)) return null;
    return Math.max(0, Math.trunc(maxNumberOfFiles));
}

export const AttachmentsSection = (props: AttachmentsSectionProps) => {
    const { requestFormContext } = props;

    const theme = useTheme();
    const request = requestFormContext.request.current;
    const settings = requestFormContext.settings.current;
    const categories = request.categories || [];

    const visibleCategories = categories.filter((category) =>
        !shouldHideCategory(category, request, settings.odvetvieId, requestFormContext.getRequestFieldsRelationTask.value?.fields ?? [])
    );
    const visibleCategoryIds = new Set(visibleCategories.map((c) => c.id));

    const files = requestFormContext.request.current.files || [];
    const uncategorizedFiles = files.filter((file) => getAttachmentFileTaxonomyId(file) == null);

    const shouldDisplaySection = visibleCategories.length > 0 || uncategorizedFiles.length > 0;
    const { isSubmitted, previewMode } = { isSubmitted: request.isSubmitted, previewMode: settings.previewMode };
    const allowChangeAttachmentTaxonomy = !isSubmitted && !previewMode;

    const { disableImmediateFileUpload } = settings;

    const attachmentsDropzonesVisible = areAttachmentUploadDropzonesGloballyVisible(request, settings);

    const previewAllowsDropZone =
        !previewMode || (settings.manualSignRequired === true && request.ismanuallySigned === false);

    const disableUpload =
        request.isSubmitted || (settings.manualSignRequired ? (request.ismanuallySigned ?? false) : false);
    const blockUploadDueToDeferredTaxonomy =
        disableImmediateFileUpload && !request.requestId;

    const handleUploadForTaxonomy = (taxonomy: TaxonomyDto, selectedFiles: File[]) => {
        const onlyFiles = selectedFiles.filter((file) => file.type !== "");
        if (!onlyFiles.length) return;
        uploadLogic(onlyFiles, requestFormContext, createRequestFormFileUploader(requestFormContext), taxonomy);
    };

    const multiTaxonomyLayout = visibleCategories.length > 2;

    const sectionHeaderSx = {
        ...getYCenterStyles(),
        gap: 1.5,
        p: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    };

    if (!shouldDisplaySection) {
        return <></>;
    }

    if (multiTaxonomyLayout) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: "12px" }}>
                {visibleCategories.map((taxonomy) => {
                    const taxonomyFiles = files.filter((file) => getAttachmentFileTaxonomyId(file) === taxonomy.id);
                    const taxonomyFileCount = taxonomyFiles.length;
                    const taxonomyMaxFilesLimit = getTaxonomyMaxFilesLimit(taxonomy);
                    const canUploadMoreFilesForTaxonomy =
                        taxonomyMaxFilesLimit === null ? true : taxonomyFileCount < taxonomyMaxFilesLimit;
                    const showDropZone =
                        attachmentsDropzonesVisible &&
                        canUploadMoreFilesForTaxonomy &&
                        previewAllowsDropZone;

                    if (taxonomyFiles.length === 0 && !showDropZone) {
                        return null;
                    }

                    return (
                        <CollapsibleSectionCard key={taxonomy.id} title={taxonomy.name ?? ""} defaultExpanded>
                            <Box
                                sx={{
                                    px: theme.spacing(3),
                                    pt: theme.spacing(3),
                                    pb: theme.spacing(3),
                                    overflow: "hidden",
                                    maxWidth: "100%",
                                }}
                            >
                                {(taxonomyFiles.length > 0 || showDropZone) && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1.5,
                                            alignContent: "flex-start",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {taxonomyFiles.map((file) => {
                                            const fileIndex = files.indexOf(file);
                                            const primaryLabel = file.taxonomy?.name || "Súbor";
                                            return (
                                                <FileTile
                                                    key={`${file.id ?? file.name}-multi-${taxonomy.id}-${fileIndex}`}
                                                    {...requestFormContext}
                                                    file={file}
                                                    index={fileIndex}
                                                    primaryLabel={primaryLabel}
                                                    showDeleteButton={!isSubmitted && !!file.uploadCompleted && !previewMode}
                                                    showChangeTaxonomyButton={allowChangeAttachmentTaxonomy}
                                                    changeTaxonomyTargets={visibleCategories}
                                                    clickedStateStrategy="index"
                                                />
                                            );
                                        })}
                                        {showDropZone && (
                                            <TaxonomyUploadZone
                                                taxonomy={taxonomy}
                                                taxonomyMaxFilesLimit={taxonomyMaxFilesLimit}
                                                taxonomyFileCount={taxonomyFileCount}
                                                disabled={disableUpload || blockUploadDueToDeferredTaxonomy}
                                                onFilesSelected={handleUploadForTaxonomy}
                                                hideTaxonomyHeading
                                                variant="section"
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </CollapsibleSectionCard>
                    );
                })}

                {uncategorizedFiles.length > 0 && (
                    <CollapsibleSectionCard title="Nekategorizované súbory" defaultExpanded>
                        <Box sx={{ px: theme.spacing(3), pt: theme.spacing(3), pb: theme.spacing(3) }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignContent: "flex-start" }}>
                                {uncategorizedFiles.map((file) => {
                                    const fileIndex = files.indexOf(file);
                                    return (
                                        <FileTile
                                            key={`${file.id ?? file.name}-uncategorized-multi-${fileIndex}`}
                                            {...requestFormContext}
                                            file={file}
                                            index={fileIndex}
                                            primaryLabel={file.taxonomy?.name || "Súbor nemá kategóriu"}
                                            showDeleteButton={!isSubmitted && !!file.uploadCompleted && !previewMode}
                                            showChangeTaxonomyButton={allowChangeAttachmentTaxonomy}
                                            changeTaxonomyTargets={visibleCategories}
                                            clickedStateStrategy="index"
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    </CollapsibleSectionCard>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "7px",
            marginTop: "12px",
        }}>
            {attachmentsDropzonesVisible && (
                <Box sx={{ ...sectionHeaderSx, "svg": { flexShrink: 0 } }}>
                    <CardDocumentIcon width={32} height={32} color={theme.palette.primary.main} />
                    <Typography variant="h6" component="p">Prílohy</Typography>
                </Box>
            )}

            {files.some((f) => fileBelongsToVisibleAttachmentCategories(f, visibleCategoryIds)) && (
                <Box
                    sx={{
                        px: theme.spacing(3),
                        pt: theme.spacing(3),
                    }}
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignContent: "flex-start" }}>
                        {files.map((file, fileIndex) => {
                            if (!fileBelongsToVisibleAttachmentCategories(file, visibleCategoryIds)) return null;
                            const primaryLabel =
                                file.taxonomy?.name ||
                                (getAttachmentFileTaxonomyId(file) == null ? "Súbor nemá kategóriu" : "Súbor");
                            return (
                                <FileTile
                                    key={`${file.id ?? file.name}-section-${fileIndex}`}
                                    {...requestFormContext}
                                    file={file}
                                    index={fileIndex}
                                    primaryLabel={primaryLabel}
                                    showDeleteButton={!isSubmitted && !!file.uploadCompleted && !previewMode}
                                    showChangeTaxonomyButton={allowChangeAttachmentTaxonomy}
                                    changeTaxonomyTargets={visibleCategories}
                                    clickedStateStrategy="index"
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            <Attachments {...requestFormContext} dropzonesOnly singleColumnOnMdDown />
        </Box>
    );
};
